import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CanvasThemePackage } from '../models/component-canvas-theme.models';

export interface ComponentCanvasThemeApiError {
  code: number;
  message: string;
  details?: string[];
}

interface CanvasThemeStateResponse {
  success: boolean;
  themePackages: CanvasThemePackage[];
}

@Injectable({
  providedIn: 'root'
})
export class ComponentCanvasThemeApiService {
  private readonly API_URL = environment.apiUrl;

  constructor(private readonly http: HttpClient) {}

  getPackages(): Observable<CanvasThemePackage[]> {
    return this.http
      .get<CanvasThemeStateResponse>(`${this.API_URL}/studio/themes`)
      .pipe(
        map((response) => response.themePackages),
        catchError(this.handleError)
      );
  }

  savePackages(themePackages: CanvasThemePackage[]): Observable<CanvasThemePackage[]> {
    return this.http
      .put<CanvasThemeStateResponse>(`${this.API_URL}/studio/themes`, { themePackages })
      .pipe(
        map((response) => response.themePackages),
        catchError(this.handleError)
      );
  }

  savePackagesKeepalive(themePackages: CanvasThemePackage[]): void {
    if (typeof fetch === 'undefined') {
      return;
    }

    void fetch(`${this.API_URL}/studio/themes`, {
      method: 'PUT',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ themePackages })
    }).catch(() => undefined);
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let message = 'An unexpected error occurred';

    if (error.error?.error?.message) {
      message = error.error.error.message as string;
    } else if (error.error?.message) {
      message = error.error.message as string;
    } else if (error.message) {
      message = error.message;
    }

    return throwError(() => ({
      code: error.status,
      message,
      details: error.error?.error?.details as string[] | undefined
    } as ComponentCanvasThemeApiError));
  };
}
